/// <reference types="vite/client" />

/** biome-ignore-all lint/security/noDangerouslySetInnerHtml: we need this to disable the flashing for theme */

import { m } from '@sohanscript/i18n';
import { localeMeta } from '@sohanscript/i18n/locale-meta';
import { TanStackDevtools } from '@tanstack/react-devtools';
import { ReactQueryDevtoolsPanel } from '@tanstack/react-query-devtools';
import {
	createRootRouteWithContext,
	HeadContent,
	Scripts,
} from '@tanstack/react-router';
import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools';
import { Providers } from '#/lib/context/providers.tsx';
import { queryClient } from '#/lib/tanstack-query/query-client.ts';
import { seo } from '@/lib/config/site';
import appCss from '../styles/globals.css?url';
import type { RouterContext } from '../types';
import '@/lib/icons/collections';
import { cn } from '@sohanemon/utils';
import { THEME_INIT_SCRIPT } from '@sohanscript/theme';
import { ThemeBackdrop, ThemeSwapOverlay } from '@sohanscript/theme/ui';
import { InjectColorFilter } from '@sohanscript/ui/color-filters';
import { Preloader } from '#/components/animated/preloader.tsx';
import { Link } from '#/components/link.tsx';

/* WARN: Inlined before external CSS loads to prevent FOUC */
const CRITICAL_CSS = `
.container {
  margin-left: auto;
  margin-right: auto;
  width: 100%;
  max-width: 72rem;
  padding-left: 0.5rem;
  padding-right: 0.5rem;

  @media (min-width: 1024px) {
    padding-left: 2rem;
    padding-right: 2rem;
  }
}
`;

const CRITICAL_SCRIPT = `
document.head.appendChild(Object.assign(document.createElement("style"),{textContent:"div[data-ts-hydrate-id]{display:contents}"}));
window.isIframe=window.self!==window.top;
`;

export const Route = createRootRouteWithContext<RouterContext>()({
	head: () => {
		// NOTE: no canonical here. It is per-page (see seo().links),
		// because a root-level link would duplicate the tag on every page.
		const fallback = seo({});
		return {
			meta: [
				{ charSet: 'utf-8' },
				{ name: 'viewport', content: 'width=device-width, initial-scale=1' },
				{ name: 'theme-color', content: '#071828' },
				...fallback.meta,
			],
			links: [
				{
					rel: 'preload',
					href: '/fonts/futura.woff2',
					as: 'font',
					type: 'font/woff2',
					crossOrigin: 'anonymous',
				},
				{
					rel: 'preload',
					href: '/lottie/setup-themed.lottie',
					as: 'fetch',
					crossOrigin: 'anonymous',
				},
				{ rel: 'stylesheet', href: appCss },
				{
					rel: 'icon',
					href: '/logo-v2.svg?v=3',
					type: 'image/svg+xml',
					'data-theme-favicon': '',
				},
				{
					rel: 'shortcut icon',
					href: '/logo-v2.svg?v=3',
					'data-theme-favicon': '',
				},
				{
					rel: 'apple-touch-icon',
					href: '/logo-v2.svg?v=3',
					'data-theme-favicon': '',
				},
				{ rel: 'manifest', href: '/manifest/manifest.webmanifest' },
			],
		};
	},
	headers: () => ({
		'Content-Security-Policy':
			"default-src 'self'; \
      script-src 'self' 'unsafe-inline' 'unsafe-eval' https://us.i.posthog.com https://us-assets.i.posthog.com https://unpkg.com/react-scan/dist/auto.global.js blob:; \
      worker-src 'self' blob:; \
      style-src 'self' 'unsafe-inline'; \
      img-src 'self' data: https:; \
      font-src 'self' data:; \
      connect-src 'self' https://us.i.posthog.com https://www.react-grab.com https://api.iconify.design https://api.simplesvg.com https://api.unisvg.com; \
      frame-ancestors 'self'; \
      frame-src 'self' https://www.youtube.com https://www.youtube-nocookie.com; \
      base-uri 'self'; \
      form-action 'self'",
	}),
	shellComponent: RootShell,
});

function RootShell({ children }: { children: React.ReactNode }) {
	const { locale, dir } = localeMeta();

	return (
		<html lang={locale} dir={dir} suppressHydrationWarning>
			<head>
				<script dangerouslySetInnerHTML={{ __html: THEME_INIT_SCRIPT }} />
				<style dangerouslySetInnerHTML={{ __html: CRITICAL_CSS }} />
				<script dangerouslySetInnerHTML={{ __html: CRITICAL_SCRIPT }} />
				{import.meta.env.DEV && (
					<script
						defer
						crossOrigin="anonymous"
						src="https://unpkg.com/react-scan/dist/auto.global.js"
					/>
				)}
				<HeadContent />
			</head>
			<body className={cn('wrap-anywhere group/body font-sans antialiased')}>
				<div id="top" className="sr-only" />
				<Preloader />
				<ThemeSwapOverlay />
				<Link className="sr-only" href="#main-content">
					{m.skip_to_main_content()}
				</Link>
				<Providers>{children}</Providers>
				<TanStackDevtools
					config={{
						openHotkey: ['Control', 'Shift', 'C'],
						triggerHidden: true,
						customTrigger: <span />,
						defaultOpen: false,
					}}
					plugins={[
						{
							name: 'Tanstack Router',
							render: <TanStackRouterDevtoolsPanel />,
						},
						{
							name: 'TanStack Query',
							render: (
								<ReactQueryDevtoolsPanel
									client={queryClient}
									theme="dark"
									hideDisabledQueries={false}
								/>
							),
							defaultOpen: false,
						},
					]}
				/>

				<InjectColorFilter name="primary" />
				<ThemeBackdrop className="fixed -z-10" />
				<Scripts />
			</body>
		</html>
	);
}
